/**
 * Annex Cloud loyalty points preview — resource route (SFCC-11016).
 *
 * Used for LIVE updates when the shopper switches variant/price on the PDP.
 * The initial (default-product) points are server-rendered by the PDP loader
 * (see product-detail-loader.ts) — this route only fires on a variant change.
 *
 * Thin delegate to `resolveEarnPoints`, which owns all site gating, the signing
 * token (server-only), SWR caching, and the Annex Cloud call. Returns ONLY the
 * points number; the token never reaches the client.
 */
import { useEffect } from "react";
import { useFetcher } from "react-router";

import { tryGetPresetContext } from "~/.server/preset-context.server";
import { resolveEarnPoints } from "~/hec/.server/annex-loyalty.server";

import { useURL } from "~/contexts";

export const loader = async ({
  request,
}: LoaderArgs): Promise<{ points: number | null }> => {
  const url = new URL(request.url);
  const productId = url.searchParams.get("pid")?.trim();
  const priceRaw = url.searchParams.get("price");
  const unitPrice = priceRaw != null ? Number(priceRaw) : NaN;
  const siteId = tryGetPresetContext()?.preset.siteId;

  if (!productId || !siteId) return { points: null };

  const points = await resolveEarnPoints({ siteId, productId, unitPrice });
  return { points };
};

export const useFetchLoyaltyPoints = () => {
  const fetcher = useFetcher<typeof loader>();
  const url = useURL();
  const load = (productId: string, unitPrice: number) => {
    fetcher.load(
      url(
        `/resources/loyalty-points?pid=${encodeURIComponent(productId)}&price=${unitPrice}`,
      ),
    );
  };
  return { load, fetcher };
};

/**
 * Convenience: fetch the points preview when the selected product id or unit
 * price changes. Effect is keyed ONLY on the inputs — `useFetcher()` returns a
 * fresh ref on every state transition, so threading it through deps would
 * re-fire the load on each idle→loading→idle cycle. (The PDP preview
 * component gates the initial call, so this only runs on an actual variant
 * change.)
 */
export const useLoyaltyPoints = (
  productId: string,
  unitPrice: number,
  enabled = true,
): number | null => {
  const { load, fetcher } = useFetchLoyaltyPoints();
  useEffect(() => {
    if (enabled && productId && Number.isFinite(unitPrice) && unitPrice > 0) {
      load(productId, unitPrice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, unitPrice, enabled]);
  // While a new (variant-change) load is in flight, `fetcher.data` still holds
  // the PREVIOUS variant's points — return null instead of mislabeling them as
  // the current selection. Resolves to the new value on settle.
  return fetcher.state === "idle" ? (fetcher.data?.points ?? null) : null;
};
